import type { AudioAsset } from '../../types';
import { getPeaks, getSamples, play, resample, type Playback } from '../../core/media/audio';
import { computeSpectrogram, type Spectrogram } from '../../core/media/spectrogram';
import { getStripView } from '../prefs';
import { paintSpectrogramStrip, spectrogramImage } from './spectrogram-image';
import { el, ensureStyle } from '../dom';
import { t } from '../../strings';

// The sound strip: tap to play, watch a playhead cross it. Hold it down and the
// clip repeats under your finger; two pills in the corner do the same thing for
// a keyboard, and halve the speed.
//
// This is the curation clip editor's canvas render (tools/curation/ui/clip-editor.ts)
// brought over to the app, and it replaces a row of flexbox <div> bars. The bars
// were cheap and read as a level meter: bottom-aligned, one lump per manifest
// peak, and progress snapped a whole bar at a time. A recording is a shape — a
// two-note call and a continuous trill should not look alike — so the strip is
// now drawn centred on its own axis, one bar per measured peak, with a playhead
// that moves per frame rather than per bar.
//
// Three details are load-bearing, all learned in the clip editor:
//
//  - The bars go on offscreen layers redrawn only when the size, theme or peaks
//    change. The playhead moves every frame, and redrawing a hundred bars plus a
//    getComputedStyle at 60fps is enough work to make the line stutter — which
//    reads as the playhead lagging the audio, the one thing it must not do.
//  - Two layers, one dim and one lit, with the lit one clipped to the played
//    fraction. That keeps "how far in am I" exact to the pixel instead of the bar.
//  - Colours are read from the CSS custom properties rather than hard-coded, so
//    the strip follows the theme swap like everything else.
//
// The strip has a second rendering, chosen on the You screen (src/app/prefs.ts):
// a SPECTROGRAM, pitch over time, which is the axis birders actually learn on
// and the one an amplitude envelope cannot show at all. It costs what the
// waveform does not — the recording has to be fetched and decoded before
// anything can be drawn — so two rules keep it honest:
//
//  - It never swaps. A strip waiting on its samples stays empty rather than
//    drawing a waveform it is about to replace; the coarse→detailed swap this
//    component used to do was visible precisely because attention was on it.
//  - It never invents. If the recording cannot be decoded, the strip falls back
//    to the waveform, which is drawn from measured peaks — it does NOT draw a
//    spectrogram of the synthetic stand-in tone, which would be a picture of a
//    bird that does not exist.

ensureStyle(
  'waveform-css',
  `
  .waveform {
    position:relative; height:72px; padding:11px 13px;
    background: var(--moss-deep);
    border:1.5px solid var(--hairline); border-radius: var(--radius-hand);
    cursor:pointer; user-select:none;
    /* The strip is held down as well as tapped now. Manipulation drops the
       double-tap zoom delay without taking the page's scrolling with it, and
       the callout rule stops iOS offering to copy a canvas after half a second
       of the gesture that is meant to be looping the clip. */
    touch-action:manipulation; -webkit-touch-callout:none;
  }
  /* A spectrogram needs height the way a waveform does not: the vertical axis
     is now pitch, and 0–10 kHz squeezed into 50px is a smear rather than a
     shape you could match against a field guide. */
  .waveform.spectro { height:108px; padding:9px 11px; }
  .waveform canvas { display:block; width:100%; height:100%; }
  .waveform .synthetic-tag, .waveform .hz-tag {
    position:absolute; top:6px;
    font-family: var(--font-mono); font-size:8px; letter-spacing:.1em;
    text-transform:uppercase; color: var(--on-dark-soft); opacity:.8;
  }
  .waveform .synthetic-tag { right:11px; }
  /* The scale, said once. The gridlines below let you count from it. */
  .waveform .hz-tag { left:12px; opacity:.65; text-shadow:0 1px 2px rgba(0,0,0,.45); }
  /* Speed and repeat, in the corner of the strip they act on.

     Over the drawing rather than under it, because the strip is fought over
     for height on every quiz screen and a row of its own would cost ~30px on
     all of them. Bottom right: the tags already hold the top corners, and the
     bars are centred on the axis, so the quietest pixels of a waveform are
     exactly here. They wear the tags' type for the same reason — this is
     instrument labelling, not a second set of buttons competing with the
     answers below. */
  .waveform .tools { position:absolute; right:9px; bottom:7px; display:flex; gap:5px; }
  .waveform .tools button {
    appearance:none; -webkit-appearance:none; cursor:pointer;
    padding:5px 8px; min-height:24px; line-height:1;
    border:1px solid var(--hairline); border-radius:999px;
    background:rgba(0,0,0,.34); color:var(--on-dark-soft);
    font-family: var(--font-mono); font-size:9px; letter-spacing:.1em;
    text-transform:uppercase;
  }
  .waveform .tools button.on {
    background:var(--honey-soft); border-color:transparent; color:var(--moss-deep);
  }
  .waveform .tools button:focus-visible { outline:2px solid var(--honey-soft); outline-offset:2px; }
`,
);

/**
 * The one slow speed on offer.
 *
 * Half, not two-thirds: the point is to hear inside a note, and a clip that is
 * merely a bit slower asks the learner to decide whether anything changed. It
 * drops the pitch an octave with it (see PlayOptions.rate) — which is why this
 * is a button you press rather than a default.
 */
const SLOW_RATE = 0.5;

/** How long a press has to last before the strip takes it as a hold. */
const HOLD_MS = 400;

/**
 * How far a press may travel and still be a hold. Below this a finger is
 * resting; above it, it is a page scroll that happened to start on the strip.
 */
const HOLD_SLOP = 10;

/** Target spacing between bar centres, in CSS pixels. */
const BAR_PITCH = 3.6;
/** Gap between bars. Below ~1px the strip fills in solid and stops reading as bars. */
const BAR_GAP = 1.1;

/**
 * Spectrogram geometry, in cells, quantized so that nudging a desktop window
 * does not re-run the transform on every frame of the drag. Columns run wider
 * than the strip has pixels and rows about double, because the image is drawn
 * scaled and smoothed: it is cheaper to oversample once than to look soft.
 */
const COLUMN_STEP = 64;
const ROW_STEP = 16;
const MIN_COLUMNS = 192;
const MAX_COLUMNS = 640;
const MIN_ROWS = 48;
const MAX_ROWS = 192;

/**
 * Computed spectrograms, by clip and geometry. Small: this exists so that
 * walking back into a species page redraws instead of re-transforming, and each
 * entry is ~100 KB of bytes.
 */
const specCache = new Map<string, Spectrogram>();
const SPEC_CACHE_MAX = 24;

export interface WaveformHandle {
  element: HTMLElement;
  toggle: () => Promise<void>;
  stop: () => void;
}

export interface WaveformOptions {
  /**
   * The clip stopped sounding without the strip being tapped again.
   *
   * Fires when the recording runs out AND when something else takes the audio
   * context away mid-clip — one sound at a time is a property of the app (see
   * core/media/audio.ts), so a second strip starting ends this one, and from
   * in here the two are indistinguishable. Anything chaining clips off this
   * has to carry its own "is this sequence still the one the learner asked
   * for" token; the side-by-side view's Play both does exactly that.
   */
  onEnd?: () => void;
}

function cssVar(name: string, fallback: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

/** One offscreen layer: every bar drawn in a single colour, centred on the axis. */
function drawLayer(
  bars: number[],
  w: number,
  h: number,
  dpr: number,
  color: string,
): HTMLCanvasElement {
  const layer = document.createElement('canvas');
  layer.width = Math.max(1, Math.round(w * dpr));
  layer.height = Math.max(1, Math.round(h * dpr));
  const g = layer.getContext('2d');
  if (!g) return layer;
  g.setTransform(dpr, 0, 0, dpr, 0, 0);
  g.fillStyle = color;
  const step = w / Math.max(1, bars.length);
  const width = Math.max(1.2, step - BAR_GAP);
  for (let i = 0; i < bars.length; i++) {
    // A floor of 2px: a silent stretch has to stay visible as part of the clip,
    // or the strip looks truncated rather than quiet.
    const bar = Math.max(2, bars[i] * h);
    g.fillRect(i * step, (h - bar) / 2, width, bar);
  }
  return layer;
}

function quantize(value: number, step: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, Math.round(value / step) * step));
}

/** A tappable sound strip that plays the recording and animates progress. */
export function waveformPlayer(
  asset: AudioAsset,
  opts: WaveformOptions = {},
): WaveformHandle {
  const canvas = el('canvas', {}) as HTMLCanvasElement;
  const wantSpec = getStripView() === 'spectrogram';
  const container = el(`div.waveform${wantSpec ? '.spectro' : ''}`, {
    'aria-label': t('species.player.play'),
  }, canvas);

  // Empty until the manifest's peaks arrive, which takes a microtask and no
  // fetch — they ride inline in region.json. A placeholder row of flat bars
  // would only be a third width of bar to swap out from under the reader.
  let peaks: number[] = [];
  let dim: HTMLCanvasElement | null = null;
  let lit: HTMLCanvasElement | null = null;
  let key = '';
  let progress = 0;

  // 'waiting' covers both "not on screen yet" and "decoding": either way the
  // strip draws nothing, because the alternative is a swap.
  let specState: 'waiting' | 'ready' | 'unavailable' = wantSpec ? 'waiting' : 'unavailable';
  let spec: Spectrogram | null = null;
  let specImage: HTMLCanvasElement | null = null;
  let specKey = '';

  const layers = (w: number, h: number, dpr: number) => {
    const theme = document.documentElement.dataset.theme ?? '';
    const next = `${Math.round(w)}|${Math.round(h)}|${dpr}|${theme}|${peaks.length}`;
    if (next === key && dim && lit) return;
    key = next;
    const bars = resample(peaks, Math.floor(w / BAR_PITCH));
    dim = drawLayer(bars, w, h, dpr, cssVar('--lichen', '#8ea376'));
    lit = drawLayer(bars, w, h, dpr, cssVar('--honey-soft', '#dfae5c'));
  };

  /**
   * How much the unplayed part recedes.
   *
   * Half for the waveform, where the bars are a shape you read at a glance and
   * dimming them costs nothing. The spectrogram cannot afford that: the picture
   * IS the content, and the state it spends nearly all its time in is unplayed.
   * At 0.5 over the panel a bird's own harmonics fall to about the brightness
   * of the background; this leaves the played side visibly ahead — with the
   * playhead saying exactly where the edge is — without making a strip nobody
   * has tapped yet the dim version of itself.
   */
  const DIM_ALPHA = 0.5;
  const SPEC_DIM_ALPHA = 0.85;

  const paintPlayhead = (g: CanvasRenderingContext2D, at: number, h: number) => {
    g.strokeStyle = cssVar('--on-dark', '#f4ece0');
    g.lineWidth = 1;
    g.beginPath();
    g.moveTo(at, 0);
    g.lineTo(at, h);
    g.stroke();
  };

  const paintSpec = (g: CanvasRenderingContext2D, w: number, h: number) => {
    const theme = document.documentElement.dataset.theme ?? '';
    if (!specImage || specKey !== theme) {
      specImage = spectrogramImage(spec!);
      specKey = theme;
    }
    paintSpectrogramStrip(g, w, h, {
      image: specImage,
      maxHz: spec!.maxHz,
      progress,
      dimAlpha: SPEC_DIM_ALPHA,
    });
  };

  const paintWave = (g: CanvasRenderingContext2D, w: number, h: number, dpr: number) => {
    layers(w, h, dpr);
    // The unplayed strip is the part you haven't heard yet, not chrome.
    g.globalAlpha = DIM_ALPHA;
    g.drawImage(dim!, 0, 0, w, h);
    g.globalAlpha = 1;
    if (progress <= 0) return;

    const at = progress * w;
    g.save();
    g.beginPath();
    g.rect(0, 0, at, h);
    g.clip();
    g.drawImage(lit!, 0, 0, w, h);
    g.restore();
    paintPlayhead(g, at, h);
  };

  const paint = () => {
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (!w || !h) return;
    const dpr = globalThis.devicePixelRatio || 1;
    const pw = Math.max(1, Math.round(w * dpr));
    const ph = Math.max(1, Math.round(h * dpr));
    if (canvas.width !== pw || canvas.height !== ph) {
      canvas.width = pw;
      canvas.height = ph;
    }
    const g = canvas.getContext('2d');
    if (!g) return;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, w, h);
    if (specState === 'ready' && spec) paintSpec(g, w, h);
    // Nothing at all while the samples are on their way: see the header.
    else if (specState !== 'waiting' && peaks.length) paintWave(g, w, h, dpr);
  };

  const setPeaks = (next: number[]) => {
    if (!next.length) return;
    peaks = next;
    key = '';
    paint();
  };

  void getPeaks(asset).then((p) => {
    setPeaks(p.peaks);
    if (p.synthetic) container.append(el('span.synthetic-tag', {}, t('species.player.demoTone')));
  });

  /**
   * Fetch, decode and transform — the expensive path, run once per strip and
   * only for a strip someone can see. Anything that goes wrong lands on the
   * waveform, which needs nothing that isn't already in memory.
   */
  const buildSpec = async () => {
    const w = canvas.clientWidth || 360;
    const h = canvas.clientHeight || 88;
    const columns = quantize(w * 1.5, COLUMN_STEP, MIN_COLUMNS, MAX_COLUMNS);
    const rows = quantize(h * 2, ROW_STEP, MIN_ROWS, MAX_ROWS);
    const id = `${asset.id}|${columns}|${rows}`;

    let made = specCache.get(id);
    if (!made) {
      const samples = await getSamples(asset);
      // A stand-in tone for a clip that claims a real recording means the fetch
      // or the decode failed. Drawing its spectrogram would be drawing a bird
      // that was never recorded, so the strip shows the measured peaks instead.
      if (!samples || (samples.synthetic && asset.file)) {
        // The taller box stays: it was committed at mount, and shrinking it
        // now would shove everything below the strip up the screen to announce
        // a fallback the waveform already makes obvious.
        specState = 'unavailable';
        paint();
        return;
      }
      made = computeSpectrogram(samples.data, samples.rate, { columns, rows });
      specCache.set(id, made);
      for (const old of specCache.keys()) {
        if (specCache.size <= SPEC_CACHE_MAX) break;
        specCache.delete(old);
      }
    }
    spec = made;
    specImage = null;
    specState = 'ready';
    container.append(
      el('span.hz-tag', {}, t('species.player.maxKhz', { khz: Math.round(made.maxHz / 1000) })),
    );
    paint();
  };

  if (wantSpec) {
    // Only what is on screen: a species page can carry a dozen recordings, and
    // eagerly decoding all of them would spend a megabyte and a chunk of main
    // thread on strips nobody has scrolled to yet.
    if (typeof IntersectionObserver === 'undefined') {
      void buildSpec();
    } else {
      const io = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          io.disconnect();
          void buildSpec();
        },
        { rootMargin: '250px' },
      );
      io.observe(container);
    }
  }

  // The canvas takes its width from CSS, which is unknown until it is in the
  // document — and changes on rotation and on a desktop window resize.
  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(() => paint()).observe(canvas);
  }

  let playback: Playback | null = null;
  let raf = 0;
  // How the NEXT clip starts, and what the two pills currently say. Both are
  // per-strip and deliberately not remembered: half speed is something you do
  // to a phrase you cannot place, not a way to listen to birds, and a repeat
  // that outlived the strip that started it would have the next question's
  // recording going round forever under an answer list.
  let rate = 1;
  let looping = false;
  // Bumped by every stop, so a play() still waiting on the audio context knows
  // the tap (or the hold) that asked for it is over.
  let gen = 0;

  const frame = () => {
    progress = playback?.progress() ?? 0;
    paint();
    // A looping clip wraps rather than arriving at 1, so this keeps running
    // across repeats — which is what the playhead crossing again looks like.
    if (progress < 1 && playback) {
      raf = requestAnimationFrame(frame);
      return;
    }
    stop();
    opts.onEnd?.();
  };

  const stop = () => {
    gen++;
    cancelAnimationFrame(raf);
    playback?.stop();
    playback = null;
    progress = 0;
    paint();
  };

  const start = async () => {
    const mine = ++gen;
    let started: Playback;
    try {
      started = await play(asset, { rate, loop: looping });
    } catch {
      // The context could not be revived — iOS backgrounds it and does not
      // always hand it back. Leave the strip idle and tappable rather than
      // wedged mid-play behind a playhead that will never move.
      progress = 0;
      paint();
      return;
    }
    // Let go before the context came back. A hold is 400 ms and reviving a
    // suspended context is not instant, so this is an ordinary sequence rather
    // than a race only a machine could run — and without it the clip starts
    // playing, unwatched, after the finger has lifted.
    if (mine !== gen) {
      started.stop();
      return;
    }
    playback = started;
    raf = requestAnimationFrame(frame);
  };

  const toggle = async () => {
    if (playback) {
      stop();
      return;
    }
    await start();
  };

  const setOn = (btn: HTMLButtonElement, on: boolean) => {
    btn.classList.toggle('on', on);
    btn.setAttribute('aria-pressed', String(on));
  };

  const tool = (label: string, title: string): HTMLButtonElement => {
    const btn = el('button', {
      type: 'button',
      title,
      'aria-label': title,
      'aria-pressed': 'false',
    }) as HTMLButtonElement;
    btn.textContent = label;
    // The strip beneath is one big play/pause target that also answers to a
    // press held down. A control sitting on top of it must be neither.
    btn.addEventListener('pointerdown', (e) => e.stopPropagation());
    return btn;
  };

  const slowBtn = tool(t('species.player.slow.label'), t('species.player.slow.title'));
  const loopBtn = tool(t('species.player.loop.label'), t('species.player.loop.title'));
  container.append(el('div.tools', {}, slowBtn, loopBtn));

  /**
   * Speed and repeat, applied to the clip in the air as well as to the next
   * one — a strip that had to be stopped and retapped to hear it slower would
   * be asking the learner to lose the phrase they are chasing.
   *
   * The pills are set from here rather than from the handlers, so that the
   * hold gesture below lights the same lamp the button does. One state, said
   * once, however it was asked for.
   */
  const setRate = (next: number) => {
    rate = next;
    playback?.setRate(next);
    setOn(slowBtn, next !== 1);
  };

  const setLooping = (on: boolean) => {
    looping = on;
    playback?.setLoop(on);
    setOn(loopBtn, on);
  };

  slowBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    setRate(rate === 1 ? SLOW_RATE : 1);
  });

  loopBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    setLooping(!looping);
    // Asking a silent strip to repeat is asking it to play: nobody presses
    // this to arrange the silence.
    if (looping && !playback) void start();
  });

  /**
   * Hold to repeat.
   *
   * The gesture the pill above is the accessible version of, and the one worth
   * having: a phrase you cannot hold in your head wants to come round again
   * immediately, and tapping a strip four times is four separate decisions.
   * Pressure is the whole control — while the finger is down the clip repeats,
   * and lifting it puts the strip back exactly where a tap would leave it,
   * silent and ready to play straight through.
   */
  let holdTimer = 0;
  let held = false;
  let from: { x: number; y: number } | null = null;
  // The click that ends a hold is not a tap, and must not toggle anything.
  let swallowClick = false;

  const cancelHold = () => {
    clearTimeout(holdTimer);
    holdTimer = 0;
    from = null;
  };

  const endHold = (fromRelease: boolean) => {
    cancelHold();
    if (!held) return;
    held = false;
    swallowClick = fromRelease;
    setLooping(false);
    stop();
  };

  container.addEventListener('pointerdown', (e) => {
    // Already repeating by the pill: there is nothing for a hold to add, and
    // taking it over would end that repeat on release.
    if (looping) return;
    from = { x: e.clientX, y: e.clientY };
    holdTimer = window.setTimeout(() => {
      held = true;
      setLooping(true);
      if (!playback) void start();
    }, HOLD_MS);
  });

  container.addEventListener('pointermove', (e) => {
    if (!from) return;
    if (Math.abs(e.clientX - from.x) > HOLD_SLOP || Math.abs(e.clientY - from.y) > HOLD_SLOP) {
      cancelHold();
    }
  });

  container.addEventListener('pointerup', () => endHold(true));
  // The gesture was taken away — the page started scrolling under it, or the
  // pointer left the strip still held. Neither produces a click to swallow.
  container.addEventListener('pointercancel', () => endHold(false));
  container.addEventListener('pointerleave', () => endHold(false));

  container.addEventListener('click', () => {
    if (swallowClick) {
      swallowClick = false;
      return;
    }
    void toggle();
  });
  // Twice: the strip is built before it is appended, so this first paint sees a
  // 0×0 box and bails, and a frame later the layout is real. The peaks arriving
  // usually beats that, but only usually — a cached region and a warm decode can
  // resolve inside the same tick that built the element.
  paint();
  requestAnimationFrame(paint);
  return { element: container, toggle, stop };
}
